import React, { useState, useEffect } from 'react';
import { Avatar, Grid, Typography, Container, Box } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InlineSpinner from 'Components/Commons/Loader/InlineSpinner';
import Api from '../../../Helpers/ApiHandler';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { URL_FORGOT_PASSWORD, URL_LOGIN } from 'Helpers/Paths';
import { LoginWrapper } from '../Pages.style';
import { API_RESET_PASSWORD } from 'Helpers/ApiPaths';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import PasswordTextField from 'Components/FormComponents/PasswordTextField';
import {
    GlobalInputLabelWrapper,
    GlobalInputLabelRequiredWrapper,
    AddButtonWrapper
} from 'Components/Global.style';
import { SERVER_ERROR } from 'Helpers/Utils';

/**
 * @constant
 * @description API handler instance for making HTTP requests.
 *
 * @type {Api}
 */
const API = new Api({});

/**
 * @constant
 * @description Regular expression for password validation.
 * Requires at least 8 characters with letters, numbers, and special characters.
 *
 * @type {RegExp}
 */
const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*+=._-])[A-Za-z\d!@#$%^&*+=._-]{8,}$/;

/**
 * @component
 * @description Main password reset component for completing password recovery.
 * Provides comprehensive password reset functionality including:
 * - New password and confirmation input
 * - Password strength validation
 * - Link expiration validation
 * - Form validation with Yup schema
 * - Password visibility toggles
 * - Loading states and error handling
 * - Automatic navigation on completion
 * - Professional UI with Material-UI components
 *
 * Key Features:
 * - Secure password reset with validation
 * - Password confirmation matching
 * - Link expiration checking
 * - Form validation with real-time feedback
 * - Password visibility toggles for both fields
 * - Loading states during API calls
 * - Error handling with toast notifications
 * - Professional authentication interface
 * - Automatic navigation on success
 * - Session cleanup on completion
 * - Time-based link validation
 *
 * @returns {JSX.Element} Complete password reset interface
 *
 * @example
 * // Used in routing configuration
 * import ResetPassword from 'Components/Pages/ResetPassword/ResetPassword';
 *
 * <Route path="/reset-password" element={<ResetPassword />} />
 *
 * @example
 * // Component usage in authentication flow
 * function AuthenticationApp() {
 *   return (
 *     <div>
 *       <ResetPassword />
 *     </div>
 *   );
 * }
 *
 * @see API_RESET_PASSWORD - Reset password API endpoint
 * @see URL_LOGIN - Login page redirect URL
 * @see URL_FORGOT_PASSWORD - Forgot password page URL
 * @see PasswordTextField - Password input component
 * @see LoginWrapper - Styled wrapper component
 */
const ResetPassword = () => {
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const [verificationToken, setverificationToken] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { state } = useLocation();

    useEffect(() => {
        if (!state) {
            navigate('/login', { replace: true });
        }
    }, [state]);

    /**
     * @constant
     * @description Initial form values for password reset form.
     * Contains default empty values for new password and confirmation fields.
     *
     * @type {Object}
     * @property {string} newPassword - New password field
     * @property {string} confirmPassword - Password confirmation field
     */
    const initialValues = {
        newPassword: '',
        confirmPassword: ''
    };

    /**
     * @constant
     * @description Yup validation schema for password reset form.
     * Validates password strength and confirmation matching.
     *
     * @type {yup.ObjectSchema}
     */
    const checkoutSchema = yup.object().shape({
        newPassword: yup
            .string()
            .strict()
            .trim('Cannot include leading and trailing space')
            .required('Password is required')
            .matches(
                passwordRegex,
                'Password must be 8 characters long and contain at least one letter, one number, and one special character'
            ),
        confirmPassword: yup
            .string()
            .strict()
            .trim('Cannot include leading and trailing space')
            .required('Confirm Password is required')
            .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    });

    useEffect(() => {
        const queryParams = queryString.parse(window.location.search);
        setverificationToken(queryParams.id);

        const utcTimeStr = queryParams.datetime;
        const utcTime = new Date(utcTimeStr);
        const currentTime = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istTime = new Date(utcTime.getTime() + istOffset);
        const newTime = new Date(istTime.getTime() + 5 * 60 * 1000);
        const newTimeCheck = newTime.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
        const currentTimeCheck = currentTime.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });

        if (!(newTimeCheck > currentTimeCheck)) {
            toast.error('The reset password link expired');
            navigate(URL_FORGOT_PASSWORD);
        }
    }, []);

    /**
     * @function
     * @description Handles form submission for password reset.
     * Processes new password and updates user account.
     *
     * @param {Object} values - Form values from Formik
     * @param {string} values.newPassword - New password value
     * @param {string} values.confirmPassword - Password confirmation value
     * @returns {void}
     *
     * @example
     * // Called when user submits password reset form
     * handleSubmit({
     *   newPassword: 'newPassword123!',
     *   confirmPassword: 'newPassword123!'
     * });
     */
    const handleSubmit = (values) => {
        setLoading(true);
        const payload = {
            email: state.email,
            password: `${values.newPassword}`
        };
        const data = [];
        data['data'] = payload;
        API.post(`${API_RESET_PASSWORD}`, data)
            .then((response) => {
                setLoading(false);
                localStorage.removeItem('rememberedEmail');
                localStorage.removeItem('rememberedPassword');
                navigate(URL_LOGIN);
                toast.success(response.message);
            })
            .catch((error) => {
                setLoading(false);
                toast.error(error?.response?.data?.message || SERVER_ERROR);
            });
    };

    /**
     * @function
     * @description Toggles new password visibility state.
     * Switches between showing and hiding new password text.
     *
     * @returns {void}
     */
    const handleClickShowNewPassword = () => {
        setShowNewPassword(!showNewPassword);
    };

    /**
     * @function
     * @description Prevents default mouse down behavior on new password field.
     * Prevents focus loss when clicking password visibility toggle.
     *
     * @param {Event} event - Mouse down event
     * @returns {void}
     */
    const handleMouseDownNewPassword = (event) => {
        event.preventDefault();
    };

    /**
     * @function
     * @description Toggles confirm password visibility state.
     * Switches between showing and hiding confirm password text.
     *
     * @returns {void}
     */
    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    /**
     * @function
     * @description Prevents default mouse down behavior on confirm password field.
     * Prevents focus loss when clicking password visibility toggle.
     *
     * @param {Event} event - Mouse down event
     * @returns {void}
     */
    const handleMouseDownConfirmPassword = (event) => {
        event.preventDefault();
    };

    return (
        <LoginWrapper>
            <Container component="main" maxWidth="xs">
                <Box
                    sx={{
                        maxWidth: '450px',
                        minWidth: '300px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '20px',
                        backgroundColor: '#ffffff',
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '10px',
                        boxShadow: '10px black'
                    }}>
                    <Avatar sx={{ m: 1, bgcolor: '#F5E0B5' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Reset Password
                    </Typography>
                    <Formik
                        onSubmit={handleSubmit}
                        initialValues={initialValues}
                        validationSchema={checkoutSchema}
                        validateOnChange={true}>
                        {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <Box style={{ marginTop: '20px' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <GlobalInputLabelWrapper>
                                                New Password{' '}
                                                <GlobalInputLabelRequiredWrapper>
                                                    {' '}
                                                    *{' '}
                                                </GlobalInputLabelRequiredWrapper>
                                            </GlobalInputLabelWrapper>
                                            <PasswordTextField
                                                size="small"
                                                showPassword={showNewPassword}
                                                margin="normal"
                                                fullWidth
                                                name="newPassword"
                                                type={showNewPassword ? 'text' : 'password'}
                                                id="newPassword"
                                                handleClickShowPassword={handleClickShowNewPassword}
                                                handleMouseDownPassword={handleMouseDownNewPassword}
                                                value={values.newPassword}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={
                                                    !!touched.newPassword && !!errors.newPassword
                                                }
                                                helperText={
                                                    touched.newPassword && errors.newPassword
                                                }
                                                props={{ placeholder: 'New password' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <GlobalInputLabelWrapper>
                                                Confirm Password
                                                <GlobalInputLabelRequiredWrapper>
                                                    {' '}
                                                    *{' '}
                                                </GlobalInputLabelRequiredWrapper>
                                            </GlobalInputLabelWrapper>
                                            <PasswordTextField
                                                size="small"
                                                showPassword={showConfirmPassword}
                                                margin="normal"
                                                fullWidth
                                                name="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                id="confirmPassword"
                                                handleClickShowPassword={
                                                    handleClickShowConfirmPassword
                                                }
                                                handleMouseDownPassword={
                                                    handleMouseDownConfirmPassword
                                                }
                                                value={values.confirmPassword}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={
                                                    !!touched.confirmPassword &&
                                                    !!errors.confirmPassword
                                                }
                                                helperText={
                                                    touched.confirmPassword &&
                                                    errors.confirmPassword
                                                }
                                                props={{ placeholder: 'Confirm password' }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <AddButtonWrapper
                                        type="submit"
                                        fullWidth
                                        disabled={loading}
                                        sx={{ mt: 3, mb: 2 }}>
                                        {loading ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 1
                                                }}>
                                                <InlineSpinner size={22} color="inherit" />
                                                Submitting…
                                            </Box>
                                        ) : (
                                            'Submit'
                                        )}
                                    </AddButtonWrapper>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            </Container>
        </LoginWrapper>
    );
};

export default ResetPassword;
